import react, { useEffect } from 'react';
import React, { useContext, useState } from 'react';
import { AppContext } from '../../AppContext';
import { Types } from '../../enums/TypeEnums';
import {
  IIndex,
  IIndicesQuery,
  IndicesDocument,
  useDeleteIndexMutation,
  useIndicesQuery
} from '../../generated/graphql';
import { canUserCreate } from '../../utils/rightUtils';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import online from '../../assets/images/actions/online.svg';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import offline from '../../assets/images/actions/offline.svg';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import stock from '../../assets/images/icons/Stocks_icon_blue.svg';
import { Box, Button } from '@mui/material';
import { Actions } from '../../enums/ActionEnums';
import OverviewTable, { IHeadCell } from '../Common/Tables/OverviewTable';
import { FormatTableData } from '../../utils/styleUtils';
import IndexForm from '../Indices/IndicesForm';
import ConstituentsForm from '../Stocks/Tabs/Constituent/ConstituentsForm';
import HistoryUpload from '../Stocks/HistoryUpload';
import excel from '../../assets/images/actions/excel_icon.svg';
import ExportHIDataForm from './ExportHIDataForm';

const IndexList: React.FunctionComponent = () => {
  const { dispatch, state } = useContext(AppContext);
  const { data, loading } = useIndicesQuery();
  const [deleteIndex] = useDeleteIndexMutation();
  const [allData, setAllData] = useState<IIndex[]>([]);
  const [index, setIndex] = useState<IIndex | null>(null);
  const [openCreateIndexForm, setOpenCreateIndexForm] = useState(false);
  const [openConstituentsForm, setOpenConstituentsForm] = useState(false);
  const canCreate = canUserCreate(state.user, Types.Index);
  const [openUpload, setOpenUpload] = useState(false);
  const [openExportHIDataForm, setOpenExportHIDataForm] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<IIndex | null>(null);

  useEffect(() => {
    if (data && data.result) {
      setAllData(data.result as IIndex[]);
    }
  }, [data]);

  const handleConstituentsClicked = (rowData: IIndex) => {
    setIndex(rowData);
    setOpenConstituentsForm(true);
  };

  const handleConstituentsClosed = () => {
    setOpenConstituentsForm(false);
  };

  const isOnlineStatus = (rowData: IIndex) => (
    <img className="m-auto" src={rowData.is_online ? online : offline} alt="mainStatus" />
  );

  const renderConstituentsColumn = (rowData: IIndex) => {
    return (
      <Button id={rowData.id} onClick={() => handleConstituentsClicked(rowData)}>
        <img src={stock} alt="excelButton" />
      </Button>
    );
  };

  const handleClose = (saved: boolean, isNew?: boolean, indexInput?: IIndex) => {
    if (saved && indexInput) {
      if (isNew) {
        addIndex(indexInput);
      }
    }
    setIndex(null);
    setOpenCreateIndexForm(false);
  };

  const addIndex = (input: IIndex) => {
    const existing = allData.find((d) => d.id === input.id);
    if (!existing) {
      setAllData([input, ...allData]);
    }
  };

  const handleDetails = (rowData: IIndex) => {
    setIndex(rowData);
    setOpenCreateIndexForm(true);
  };

  const handleRemove = (rowData: IIndex) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete ${rowData.name} index?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleDelete = (rowData: IIndex) => {
    dispatch({
      type: Actions.HideConfirmation
    });
    deleteIndex({
      variables: {
        id: rowData.id
      },
      update(cache) {
        const indexListQuery = cache.readQuery({
          query: IndicesDocument
        }) as IIndicesQuery;
        if (indexListQuery.result) {
          const cachedIndex = indexListQuery.result.findIndex((i) => i?.id === rowData.id);
          if (cachedIndex > -1) {
            const data = indexListQuery.result.filter((i) => i?.id !== rowData.id);
            cache.writeQuery({
              query: IndicesDocument,
              data: { result: data }
            });
          }
        }
      }
    }).catch((e) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: { text: e.message, severity: 'error' }
      });
    });
    dispatch({
      type: Actions.ShowMessage,
      payload: {
        text: `${rowData.name} index deleted`,
        severity: 'success'
      }
    });
  };

  const renderExportHIData = (rowData: IIndex) => {
    return (
      <Button
        id={rowData.id}
        sx={{ width: 'auto' }}
        onClick={() => {
          setOpenExportHIDataForm(true);
          setSelectedIndex(rowData);
        }}
      >
        <img src={excel} alt="removeButton" />
      </Button>
    );
  };
  const columns = [
    {
      title: 'Details',
      width: 64,
      sorting: false
    },
    {
      title: 'ID',
      field: 'id',
      type: 'numeric',
      width: 53
    },
    {
      title: 'Constituents',
      width: 101,
      render: renderConstituentsColumn
    },
    {
      title: 'Online',
      field: 'is_online',
      render: isOnlineStatus,
      type: 'boolean',
      width: 61
    },

    {
      title: 'Country',
      field: 'country.name',
      width: 110
    },

    {
      title: 'Local Currency',
      field: 'currency.code',
      width: 135
    },

    {
      title: 'Code',
      field: 'code',
      width: 111
    },

    {
      title: 'Net Code',
      field: 'net_code',
      width: 137
    },
    {
      title: 'Name',
      field: 'name',
      width: 500
    },
    {
      title: 'Name Identifier',
      field: 'identifier',
      width: 377
    },
    {
      title: 'Export HI Data',
      width: 30,
      sorting: false,
      render: renderExportHIData
    },
    {
      title: 'Remove',
      width: 71,
      sorting: false
    }
  ] as IHeadCell[];

  return (
    <Box className="overviewPage">
      <OverviewTable
        columns={columns}
        data={FormatTableData(allData)}
        loading={loading}
        handleDetails={handleDetails}
        handleRemove={handleRemove}
        pageSize={15}
        createButtonProps={{
          id: 'create-index-button',
          onClick: () => setOpenCreateIndexForm(true),
          text: 'Create index',
          disabled: !canCreate
        }}
        importButtonProps={{
          id: 'import-index-data',
          onClick: () => setOpenUpload(true)
        }}
      />
      {openCreateIndexForm && <IndexForm handleClose={handleClose} indexInput={index} />}
      {openConstituentsForm && (
        <ConstituentsForm handleClose={handleConstituentsClosed} indexId={index?.id as string} />
      )}
      {openUpload && <HistoryUpload handleClose={() => setOpenUpload(false)} isIndexFile={true} />}
      {openExportHIDataForm && (
        <ExportHIDataForm
          handleClose={() => {
            setOpenExportHIDataForm(false);
            setSelectedIndex(null);
          }}
          selectedIndex={selectedIndex}
        />
      )}
    </Box>
  );
};

export default IndexList;
